import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import CDN_BASE, { cdnPhoto } from '../../../utils/cdn';

const featuredTrucks = { featured: true };

function BirthdayPartyCatering() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();
  const { content: cms } = usePageContent('catering_birthday');

  return (
    <div className='CateringPage'>
      <Meta
        title={cms?.meta?.title || `Birthday Party Food ${vehicle_type} Catering`}
        type='website'
        canonical='/food-truck-catering/birthday-parties'
        image={cdnPhoto(`${citySlug}-community-food-truck-event`)}
        description={cms?.meta?.description || `Book a food ${vehicleType} for a birthday party in ${city_name}. Kids' parties, backyard cookouts, and milestone birthdays. Requesting quotes is free.`}
        structuredData={{
          cateringService: {
            name: `Birthday Party Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book a food ${vehicleType} for your birthday party in ${city_name}. Kids' parties to milestone celebrations.`,
            url: '/food-truck-catering/birthday-parties',
            serviceType: 'Birthday Party Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Birthday Party Catering', item: '/food-truck-catering/birthday-parties' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-community-food-truck-event.webp`} alt='Food truck at a birthday party celebration' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `Food ${vehicleType} catering for birthday parties`}</h1>
            <CmsRichText
              html={cms?.hero?.subheadline}
              fallback={
                <p>Skip the pizza boxes. A local {vehicleType} parks out front, cooks to order, and cleans up. It works for a kids&#39; party and for a 50th.</p>
              }
            />
            <p>
              <Link to='/catering/requests/new' className='Button Button--mobile-w100'>{cms?.hero?.cta_label || 'Get party catering quotes'}</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Featured food ${vehicleType}s`} options={featuredTrucks} />

      <WhyBookWithUs />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Kids&#39; parties and grown-up ones</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-buffet.webp`} alt='Kids enjoying food truck at a birthday party' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Kids&#39; parties</h3>
            <p>Sliders, mac and cheese, churros, ice cream. Kids order at the window, which is half the fun, and parents don&#39;t spend the party in the kitchen. If it&#39;s a <Link to='/food-truck-catering/private-parties'>backyard party</Link>, check that there&#39;s room for the {vehicleType} to park.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-catering-setup.webp`} alt='Food truck setup for a milestone celebration' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>30th, 40th, 50th, and beyond</h3>
            <p>Pick the food that fits the person: a taco bar, BBQ, wood-fired pizza, or a dessert {vehicleType} after dinner. Many {vehicleType}s offer drink and dessert add-ons. Not sure what it&#39;ll cost? <Link to='/food-truck-catering/prices'>See how catering is priced</Link> before you request quotes.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title='What customers say' />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Ready to find a ${vehicleType} for the party?`} />
      </section>
    </div>
  );
}

export default BirthdayPartyCatering;
